/**
 * Where the canvas providers report a failure that ends the page: a canvas
 * that won't load, has no room, or can't reach it. A no-op by default; the
 * capture page turns it into `__CANVAS_CAPTURE_FAILED__` for the
 * canvas-screenshot Lambda.
 */
import { getApiErrorStatus } from "@/api/utils";
import { createContext, useContext, useEffect } from "react";

export type CanvasFailureCode =
  | "missing_params"
  | "unauthorized"
  | "forbidden"
  | "not_found"
  | "no_room"
  | "api_error"
  | "liveblocks"
  | "render_error";

export type ReportCanvasFailure = (
  code: CanvasFailureCode,
  detail: string,
) => void;

export const CanvasFailureContext = createContext<ReportCanvasFailure>(
  () => {},
);

export function useReportCanvasFailure(): ReportCanvasFailure {
  return useContext(CanvasFailureContext);
}

/** Reports from a render branch that has no other effect. */
export function CanvasFailed({
  code,
  detail,
}: {
  code: CanvasFailureCode;
  detail: string;
}) {
  const report = useReportCanvasFailure();
  useEffect(() => report(code, detail), [report, code, detail]);
  return null;
}

export function canvasFailureCodeForError(error: unknown): CanvasFailureCode {
  switch (getApiErrorStatus(error)) {
    case 401:
      return "unauthorized";
    case 403:
      return "forbidden";
    case 404:
      return "not_found";
    default:
      return "api_error";
  }
}
